import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import { FaFilter, FaSearch, FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { LABEL, MESSAGE } from '../../../../constants'
import deepEqual from 'fast-deep-equal'

const FilterPopoverWithCheck = ({
  label = LABEL.FILTER.label,
  initialSelected = [],
  onSubmit,
  fetchItems,
  displayField = 'name',
  extractValue = (item) => item,
  pageSize = 10,
  selectedPageSize = 5,
  content = false, // daca vine din response.content sau response.data.content
  cif = null,
  isScrollable = false,
  extraFilters = {},
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [localSelected, setLocalSelected] = useState([])
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [pageNumberAll, setPageNumberAll] = useState(0)
  const [totalPagesAll, setTotalPagesAll] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [pageNumberSelected, setPageNumberSelected] = useState(0)

  const loadData = useCallback(async () => {
    if (!isOpen) return
    setIsLoading(true)
    try {
      const params = {
        searchTerm,
        cif,
        orderByList: [
          {
            field: 'name',
            direction: 'ASCENDING'
          }
        ],
        ...extraFilters
      }
      if (!isScrollable) {
        params.pageNumber = pageNumberAll
        params.size = pageSize
      }

      const response = await fetchItems(params)
      if (response) {
        const items = content ? response.content : response.data.content
        setData(items || [])
        if (!isScrollable) {
          const total = content ? response.total : response.data.total
          setTotalPagesAll(Math.ceil(total / pageSize))
        }
      }
    } catch (err) {
      console.error(MESSAGE.FILTER_LOADING_ERROR, err)
    } finally {
      setIsLoading(false)
    }
  }, [isOpen, fetchItems, searchTerm, pageNumberAll, pageSize, content, cif, isScrollable])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    setPageNumberAll(0)
  }, [searchTerm])

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(initialSelected)
      setPageNumberSelected(0)
    }
  }, [isOpen, initialSelected])

  const isItemSelected = useCallback(
    (item) => {
      const val = extractValue(item)
      // return localSelected.some((selected) => extractValue(selected) === val)
      return localSelected.some((selected) => deepEqual(extractValue(selected), val))
    },
    [extractValue, localSelected]
  )

  const handleToggle = useCallback(
    (item) => {
      if (isItemSelected(item)) {
        setLocalSelected((previous) =>
          previous.filter((selected) => !deepEqual(extractValue(selected), extractValue(item)))
        )
      } else {
        setLocalSelected((previous) => [...previous, item])
      }
    },
    [isItemSelected, extractValue]
  )

  const totalSelected = localSelected.length
  const totalPagesSelected = Math.ceil(totalSelected / selectedPageSize)
  const selectedStart = pageNumberSelected * selectedPageSize
  const selectedEnd = selectedStart + selectedPageSize
  const pageSelectedItems = localSelected.slice(selectedStart, selectedEnd)

  const handleClearAllSelected = () => {
    setLocalSelected([])
    setPageNumberSelected(0)
  }

  const visibleData = data.filter((item) => !isItemSelected(item))

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value)
    setPageNumberAll(0)
  }, [])

  const handleSubmit = useCallback(() => {
    const finalCifs = localSelected.map((selected) => extractValue(selected))
    onSubmit(finalCifs)
    onClose()
  }, [localSelected, extractValue, onSubmit, onClose])

  const renderPageControls = (totalPages, pageNumber, setPageNumber) => {
    if (isScrollable) return null
    return (
      totalPages > 1 && (
        <Flex justify="space-between" align="center" mt={2} mb={2}>
          <Button
            size="xs"
            leftIcon={<FaChevronLeft />}
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 0))}
            isDisabled={pageNumber <= 0}
          >
            {LABEL.PREVIOUS_PAGE.label}
          </Button>
          <Text fontSize="xs">
            Pag. {pageNumber + 1} / {totalPages}
          </Text>
          <Button
            size="xs"
            rightIcon={<FaChevronRight />}
            onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages - 1))}
            isDisabled={pageNumber >= totalPages - 1}
          >
            {LABEL.NEXT_PAGE.label}
          </Button>
        </Flex>
      )
    )
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      closeOnBlur={false}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <Button size="sm" ml="2" variant="ghost">
          <Icon
            as={FaFilter}
            color={initialSelected.length > 0 ? 'blue.500' : 'gray.500'}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent minW="320px" maxW="420px">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="bold">{label}</PopoverHeader>

        <PopoverBody>
          {/* Selected Items Section */}
          <Flex align="center" justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="semibold">
              {LABEL.SELECTED_ELEMENTS.label} ({totalSelected})
            </Text>
            {totalSelected > 0 && (
              <Button size="xs" variant="outline" onClick={handleClearAllSelected}>
                {LABEL.DELETE.label}
              </Button>
            )}
          </Flex>

          {totalSelected === 0 ? (
            <Text fontSize="sm" color="gray.500" mb={3}>
              {LABEL.NO_ELEMENTS_SELECTED.label}
            </Text>
          ) : (
            <>
              {pageSelectedItems.map((selected) => {
                const val = extractValue(selected)
                const labelVal =
                  typeof selected === 'string' ? selected : selected[displayField]
                return (
                  <Flex key={val} align="center" mb={1}>
                    <Checkbox isChecked onChange={() => handleToggle(selected)}>
                      <Text fontSize="sm" ml={2}>
                        {labelVal}
                      </Text>
                    </Checkbox>
                  </Flex>
                )
              })}
              {renderPageControls(totalPagesSelected, pageNumberSelected, setPageNumberSelected)}
            </>
          )}

          <Divider my={3} />

          {/* All Items Section */}
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            Toate elementele
          </Text>

          <Flex align="center" mb={2}>
            <Icon as={FaSearch} color="gray.500" mr={2} />
            <Input
              placeholder={LABEL.SEARCH.label}
              size="sm"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </Flex>

          {isLoading ? (
            <Box textAlign="center" p={2}>
              <Spinner size="sm" />
            </Box>
          ) : visibleData.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center">
              {LABEL.EMPTY_FILTER.label}
            </Text>
          ) : (
            <Box
              maxH={isScrollable ? '200px' : 'auto'}
              overflowY={isScrollable ? 'auto' : 'visible'}
            >
              {visibleData.map((item) => {
                const val = extractValue(item)
                const labelVal =
                  typeof item === 'string' ? item : item[displayField]
                return (
                  <Flex key={val} align="center" mb={1}>
                    <Checkbox
                      isChecked={false}
                      onChange={() => handleToggle(item)}
                    >
                      <Text fontSize="sm" ml={2}>
                        {labelVal}
                      </Text>
                    </Checkbox>
                  </Flex>
                )
              })}
            </Box>
          )}

          {renderPageControls(totalPagesAll, pageNumberAll, setPageNumberAll)}

          <Flex justify="flex-end" mt={4}>
            <Button size="sm" colorScheme="blue" onClick={handleSubmit}>
              {LABEL.APPLY.label}
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default FilterPopoverWithCheck
